// Helper to determine if a color is light (for text contrast)
export function isLightColor(color: string): boolean {
  if (!color) return true; // Default to light if no color

  // Handle hex colors
  let hex = color.replace('#', '');
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  // Handle invalid hex
  if (hex.length !== 6) return true;

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Handle NaN
  if (isNaN(r) || isNaN(g) || isNaN(b)) return true;

  // Calculate luminance - lower threshold to catch more light colors
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6;
}

// Check if color is very light (white or near-white)
export function isVeryLightColor(color: string): boolean {
  if (!color) return true;

  let hex = color.replace('#', '');
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  if (hex.length !== 6) return true;

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  if (isNaN(r) || isNaN(g) || isNaN(b)) return true;

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.85;
}

// Get contrasting text color for a background
export function getContrastColor(bgColor: string): string {
  return isLightColor(bgColor) ? '#1f2937' : '#ffffff';
}

// Get styling for team color badges/elements
// Returns: { backgroundColor, color, border }
export function getTeamColorStyle(teamColor: string): {
  backgroundColor: string;
  color: string;
  border: string;
} {
  const veryLight = isVeryLightColor(teamColor);
  const light = isLightColor(teamColor);

  if (veryLight) {
    // Very light/white: use light gray background with dark text and gray border
    return {
      backgroundColor: '#f3f4f6', // gray-100
      color: '#1f2937', // gray-800
      border: '2px solid #6b7280', // gray-500
    };
  } else if (light) {
    // Light but not white: use the color but with dark text and border
    return {
      backgroundColor: teamColor,
      color: '#1f2937', // gray-800
      border: '2px solid #6b7280', // gray-500
    };
  } else {
    // Dark color: use color with white text, no border needed
    return {
      backgroundColor: teamColor,
      color: '#ffffff',
      border: 'none',
    };
  }
}

// Get a visible version of the color for use as text on white background
export function getVisibleTextColor(color: string): string {
  if (!color) return '#1f2937';

  // Handle hex colors
  let hex = color.replace('#', '');
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  if (hex.length !== 6) return '#1f2937';

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  if (isNaN(r) || isNaN(g) || isNaN(b)) return '#1f2937';

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // If the color is too light to be visible as text on white background,
  // return dark gray instead
  if (luminance > 0.6) {
    return '#374151'; // gray-700
  }

  return color;
}

// Get styling for small indicators (dots, small badges)
export function getTeamIndicatorStyle(teamColor: string): {
  backgroundColor: string;
  border: string;
} {
  const veryLight = isVeryLightColor(teamColor);
  const light = isLightColor(teamColor);

  if (veryLight) {
    // Very light/white: use gray color with border
    return {
      backgroundColor: '#9ca3af', // gray-400
      border: '2px solid #6b7280', // gray-500
    };
  } else if (light) {
    // Light but not white: use team color with border
    return {
      backgroundColor: teamColor,
      border: '2px solid #6b7280', // gray-500
    };
  } else {
    // Dark color: use team color, no border needed
    return {
      backgroundColor: teamColor,
      border: 'none',
    };
  }
}
